import React, { useEffect, useState } from "react";
import { Row, Col, Input, InputNumber, Select, DatePicker, Checkbox, Spin } from "antd";
import dayjs from "dayjs";
import axios from "axios";
import { API_CONST } from "../../../../const";
import useLocationOptions from "../../../../hooks/useLocationOptions";

const BasicDetails = ({ formData, onChange, setIsValid, customerOrganizations = [] }) => {
  const [engagements, setEngagements] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingEngagements, setLoadingEngagements] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const { countries, cities, fetchCities } = useLocationOptions(formData.reportingCountry || "India");

  // Fetch engagements when customer organization changes
  useEffect(() => {
    const fetchEngagements = async () => {
      if (!formData.customerOrganization) {
        setEngagements([]);
        return;
      }
      setLoadingEngagements(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.post(
          API_CONST.GET_ENGAGEMENTS,
          { customerOrganization: formData.customerOrganization, status: 'active' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEngagements(response.data.engagements || []);
      } catch (error) {
        console.error("Error fetching engagements:", error);
        setEngagements([]);
      } finally {
        setLoadingEngagements(false);
      }
    };
    fetchEngagements();
  }, [formData.customerOrganization]);

  // Fetch projects when engagement changes
  useEffect(() => {
    const fetchProjects = async () => {
      if (!formData.engagementId) {
        setProjects([]);
        return;
      }
      setLoadingProjects(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.post(
          API_CONST.GET_PROJECTS,
          { engagementId: formData.engagementId, status: 'active' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProjects(response.data.projects || []);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, [formData.engagementId]);

  // Pre-fill fields when engagement is selected
  const handleEngagementChange = (engagementId) => {
    const selectedEngagement = engagements.find(e => e.id === engagementId);
    onChange("engagementId", engagementId);
    onChange("projectId", null); // Reset project when engagement changes
    
    if (selectedEngagement) {
      // Pre-fill duration from engagement
      if (selectedEngagement.expectedDuration && selectedEngagement.durationUnit) {
        const durationText = `${selectedEngagement.expectedDuration} ${selectedEngagement.durationUnit === 'months' ? (selectedEngagement.expectedDuration === 1 ? 'Month' : 'Months') : selectedEngagement.durationUnit}`;
        onChange("engagementMonths", durationText);
      }
      // Pre-fill engagement type
      if (selectedEngagement.engagementType) {
        onChange("engagementType", selectedEngagement.engagementType);
      }
    }
  };

  const validateFields = () => {
    const requiredFields = [
      "customerOrganization",
      "engagementId",
      "budget",
      "location",
      "engagementMonths",
      "engagementType",
      "requirementCount",
      "startDate",
      "communication",
    ];

    const isValid = requiredFields.every((field) => {
      if (field === "startDate") {
        const dateValue = formData[field];
        if (!dateValue) return false;
        if (typeof dateValue === 'string') {
          return dayjs(dateValue).isValid();
        }
        return dateValue && typeof dateValue.isValid === 'function' ? dateValue.isValid() : Boolean(dateValue);
      }
      return (
        formData[field] !== undefined &&
        formData[field] !== null &&
        formData[field] !== ""
      );
    });

    setIsValid(isValid);
  };

  useEffect(() => {
    validateFields();
  }, [formData]);

  const engagementMonths = [
    "1 Month",
    "2 Months",
    "3 Months",
    "6 Months",
    "9 Months",
    "12 Months",
    "18 Months",
    "24 Months",
  ];

  const engagementTypes = [
    "Fixed Price Project",
    "Time & Material",
    "Dedicated Team",
    "Staff Augmentation",
    "Managed Services",
    "Consulting",
  ];

  const communicationLevels = [
    "Excellent",
    "Good",
    "Average",
    "Basic",
  ];

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <label>Customer Organization *</label>
          <Select
            placeholder="Select customer organization"
            value={formData.customerOrganization || undefined}
            onChange={(value) => {
              onChange("customerOrganization", value);
              onChange("engagementId", null);
              onChange("projectId", null);
            }}
            style={{ width: "100%" }}
            showSearch
            options={customerOrganizations.map((org) => ({ label: org.name, value: org.name }))}
          />
        </Col>
        <Col span={8}>
          <label>Engagement *</label>
          <Select
            placeholder="Select engagement"
            value={formData.engagementId || undefined}
            onChange={handleEngagementChange}
            style={{ width: "100%" }}
            showSearch
            disabled={!formData.customerOrganization}
            loading={loadingEngagements}
            notFoundContent={loadingEngagements ? <Spin size="small" /> : "No active engagements"}
            options={engagements.map((eng) => ({ label: `${eng.id} - ${eng.title}`, value: eng.id }))}
          />
        </Col>
        <Col span={8}>
          <label>Project (Optional)</label>
          <Select
            placeholder="Select project"
            value={formData.projectId || undefined}
            onChange={(value) => onChange("projectId", value)}
            style={{ width: "100%" }}
            showSearch
            allowClear
            disabled={!formData.engagementId}
            loading={loadingProjects}
            notFoundContent={loadingProjects ? <Spin size="small" /> : "No active projects"}
            options={projects.map((proj) => ({ label: `${proj.id} - ${proj.title}`, value: proj.id }))}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={6}>
          <label>Budget *</label>
          <InputNumber
            placeholder="Enter your price"
            value={formData.budget}
            onChange={(value) => onChange("budget", value)}
            style={{ width: "100%" }}
            min={0}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => (value ? value.replace(/,/g, "") : "")}
          />
          <span style={{ fontSize: 12, color: "#666" }}>₹ (this Price will be shown to Developers)</span>
        </Col>

        <Col span={6}>
          <label>Budget Period</label>
          <Select
            placeholder="Per/month"
            value={formData.budgetPeriod || "Per/month"}
            onChange={(value) => onChange("budgetPeriod", value)}
            style={{ width: "100%" }}
            options={[
              { value: "Per/month", label: "Per/month" },
              { value: "Per/hour", label: "Per/hour" },
              { value: "Fixed Price", label: "Fixed Price" },
            ]}
          />
        </Col>

        <Col span={6}>
          <label>Reporting Country *</label>
          <Select
            placeholder="Select country"
            value={formData.reportingCountry || undefined}
            onChange={(value) => {
              onChange("reportingCountry", value);
              fetchCities(value);
              if (value === "Remote") {
                onChange("location", "Remote");
              } else {
                onChange("location", undefined);
              }
            }}
            style={{ width: "100%" }}
            showSearch
            options={countries.map((c) => ({ label: c.label, value: c.value }))}
          />
        </Col>

        <Col span={6}>
          <label>Reporting Location *</label>
          <Select
            placeholder="Select city"
            value={formData.location || undefined}
            onChange={(value) => onChange("location", value)}
            style={{ width: "100%" }}
            showSearch
            options={cities.map((c) => ({ label: c.label, value: c.value }))}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={6}>
          <label>Month of Engagement *</label>
          <Select
            placeholder="3 Months"
            value={formData.engagementMonths}
            onChange={(value) => onChange("engagementMonths", value)}
            style={{ width: "100%" }}
            options={engagementMonths.map((m) => ({ label: m, value: m }))}
          />
        </Col>

        <Col span={6}>
          <label>Engagement Type *</label>
          <Select
            placeholder="Full-Time Contract"
            value={formData.engagementType}
            onChange={(value) => onChange("engagementType", value)}
            style={{ width: "100%" }}
            options={engagementTypes.map((t) => ({ label: t, value: t }))}
          />
        </Col>

        <Col span={6}>
          <label>No of Requirements *</label>
          <InputNumber
            placeholder="Enter number"
            min={1}
            value={formData.requirementCount}
            onChange={(value) => onChange("requirementCount", value)}
            style={{ width: "100%" }}
          />
        </Col>

        <Col span={6}>
          <label>Requirements Period</label>
          <Select
            placeholder="Per/month"
            value={formData.requirementPeriod || "Per/month"}
            onChange={(value) => onChange("requirementPeriod", value)}
            style={{ width: "100%" }}
            options={[
              { value: "Per/month", label: "Per/month" },
              { value: "Total", label: "Total" },
            ]}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={8}>
          <label>Tentative Start Date *</label>
          <DatePicker
            placeholder="Select start date"
            value={formData.startDate ? (typeof formData.startDate === 'string' ? dayjs(formData.startDate) : formData.startDate) : null}
            onChange={(date) => onChange("startDate", date)}
            style={{ width: "100%" }}
          />
        </Col>

        <Col span={8}>
          <label>Communication (EN) *</label>
          <Select
            placeholder="Excellent"
            value={formData.communication}
            onChange={(value) => onChange("communication", value)}
            style={{ width: "100%" }}
            options={communicationLevels.map((level) => ({ label: level, value: level }))}
          />
        </Col>

        <Col span={8}>
          <label>&nbsp;</label>
          <div style={{ marginTop: 8 }}>
            <Checkbox
              checked={formData.worldwide}
              onChange={(e) => onChange("worldwide", e.target.checked)}
            >
              Worldwide
            </Checkbox>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default BasicDetails;
